/**
 * Moteur de synchronisation (ADR-004, Q115–Q123, Q153) :
 * - enqueueOperation() : mutation hors ligne journalisée (uuid client) ;
 * - push() : envoi par lots idempotent, statuts visibles (Q117) ;
 * - pull() : delta incrémental du périmètre préchargé, par pages, puis retrait
 *   de ce qui en est sorti (Q115, FLD-3) ;
 * - uploadPendingMedia() : reprise des médias (Q120) ;
 * - readEntity() : état local fusionné (cache + patchs en attente).
 * Jamais de perte silencieuse : chaque opération a un statut consultable.
 * Le stockage passe par LocalStore (ADR-010) : Dexie sur web, SQLite chiffré
 * sur mobile — le moteur est identique sur toutes les plateformes. Chaque
 * compte a sa base (WEB1-01) ; `releaseAccountData()` la range à la déconnexion.
 */
import { api, ApiError, uploadFile } from '../core/api.js';
import { shrinkForUpload, uploadFilename } from '../core/image-upload.js';
import { clearClosureKeys } from './closure-key.js';
import {
  activeStoreOwner, countWaitingIn, destroyLocalStore, getLocalStore, openLocalStore, selectStoreScope,
  type CachedEntity, type LocalStore, type OutboxOperation, type PendingMedia, type StoreScope,
} from './store.js';

export type SyncStatus = 'ok' | 'pending' | 'offline' | 'error';

export interface SyncState {
  status: SyncStatus;
  pendingOps: number;
  pendingMedia: number;
  /** Gestes et fichiers refusés par le serveur : ils ne repartent plus seuls, ils attendent une correction. */
  refused: number;
  conflicts: number;
  lastSyncAt?: string;
  lastError?: string;
}

type Listener = (state: SyncState) => void;
const listeners = new Set<Listener>();
const IDLE: SyncState = { status: 'ok', pendingOps: 0, pendingMedia: 0, refused: 0, conflicts: 0 };
let current: SyncState = IDLE;

function notify(partial: Partial<SyncState>): void {
  current = { ...current, ...partial };
  for (const listener of listeners) listener(current);
}

export function onSyncState(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

export function getSyncState(): SyncState {
  return current;
}

async function refreshCounters(): Promise<void> {
  try {
    const store = await getLocalStore();
    const pendingOps = await store.countOperations(['attente', 'erreur', 'envoi']);
    const pendingMedia = await store.countMedia(['attente', 'erreur', 'envoi']);
    // Un fichier refusé compte aussi : la note ou le rapport est parti sans lui (décision 6).
    const refused = (await store.countOperations(['refusee'])) + (await store.countMedia(['refusee']));
    notify({ pendingOps, pendingMedia, refused });
  } catch {
    // Base fermée (compte qui vient de se déconnecter) : le prochain compte recompte.
  }
}

/** Journalise une mutation hors ligne et met à jour le cache local. */
export async function enqueueOperation(
  op: Omit<OutboxOperation, 'opId' | 'createdAt' | 'status' | 'attempts' | 'ownerUserId'>,
): Promise<string> {
  const ownerUserId = activeStoreOwner();
  const store = await getLocalStore();
  const opId = crypto.randomUUID();
  await store.addOperation({
    ...op,
    opId,
    createdAt: new Date().toISOString(),
    status: 'attente',
    attempts: 0,
    ...(ownerUserId ? { ownerUserId } : {}),
  });
  // Optimistic update du cache
  const cached = await store.getEntity(op.entityId);
  if (cached && op.opKind === 'update' && op.entityKind !== 'time_entry') {
    await store.putEntity({
      ...cached,
      data: {
        ...cached.data,
        ...op.patch,
      },
      updatedAt: new Date().toISOString(),
    });
  }
  // Transition terrain hors ligne : la fiche montre l'étape suivante tout de suite.
  if (
    cached &&
    op.entityKind === 'intervention' &&
    op.opKind === 'action' &&
    op.patch.action === 'transition' &&
    typeof op.patch.to === 'string'
  ) {
    await store.putEntity({
      ...cached,
      data: { ...cached.data, status: op.patch.to },
      updatedAt: new Date().toISOString(),
    });
  }
  // Rapport commencé en ligne depuis le dernier pull : l'appareil n'en a pas
  // de copie. Il en garde une avec la saisie, pour la relire hors ligne après
  // un redémarrage (WEB1-06) ; le prochain pull la remplace.
  if (!cached && op.opKind === 'update' && op.entityKind === 'report' && typeof op.patch.intervention_id === 'string') {
    await store.putEntity({
      id: op.entityId,
      kind: 'report',
      data: { ...op.patch, id: op.entityId, status: 'brouillon', pending: true },
      version: 0,
      updatedAt: new Date().toISOString(),
    });
  }
  // Une création hors ligne est visible tout de suite, marquée « en attente »
  // jusqu'au prochain pull qui la remplacera par la version du serveur.
  if (!cached && op.opKind === 'create') {
    await store.putEntity({
      id: op.entityId,
      kind: op.entityKind,
      data: { ...op.patch, id: op.entityId, pending: true, created_at: new Date().toISOString() },
      version: 0,
      updatedAt: new Date().toISOString(),
    });
  }
  await refreshCounters();
  void push(); // tentative immédiate si en ligne
  return opId;
}

/** Capture only prior refusals; acknowledge them after a newer correction succeeds. */
export async function captureTimeCorrectionRefusals(
  entityId: string, throughOpId?: string,
): Promise<() => Promise<void>> {
  const store = await getLocalStore();
  const operations = await store.listOperationsForEntity(entityId, ['refusee', 'envoi']);
  const boundary = throughOpId ? operations.findIndex((op) => op.opId === throughOpId) : operations.length;
  const prior = operations.slice(0, boundary < 0 ? 0 : boundary)
    .filter((op) => op.entityKind === 'time_entry' && op.opKind === 'update' && op.status === 'refusee');
  return async () => {
    for (const op of prior) await store.updateOperation(op.opId, { status: 'succes' });
  };
}

/** Création de ligne de temps refusée par le serveur, gardée sur l'appareil. */
export async function refusedTimeCreate(entityId: string): Promise<OutboxOperation | undefined> {
  const store = await getLocalStore();
  const operations = await store.listOperationsForEntity(entityId, ['refusee']);
  return operations.find((op) => op.entityKind === 'time_entry' && op.opKind === 'create');
}

/** Opération refusée puis reprise autrement : elle ne s'affiche plus comme refusée. */
export async function acknowledgeOperation(opId: string): Promise<void> {
  const store = await getLocalStore();
  await store.updateOperation(opId, { status: 'succes' });
  await refreshCounters();
}

/**
 * Envoi ou pull en cours, avec le compte pour lequel il tourne. Un appel pour
 * le même compte rejoint le travail en cours ; pour un autre compte, il
 * s'enchaîne après lui au lieu de se résoudre sur le travail de l'ancien.
 */
interface AccountRunner {
  start(): Promise<void>;
  /** Travail en cours (le dernier enchaîné), s'il y en a un. */
  running(): Promise<void> | undefined;
}

/** Le compte servi a-t-il changé depuis `owner` (déconnexion, autre compte) ? */
function accountChanged(owner: string | undefined): boolean {
  return activeStoreOwner() !== owner;
}

function accountRunner(task: (owner: string | undefined) => Promise<void>): AccountRunner {
  let current: { owner: string | undefined; run: Promise<void> } | undefined;
  return {
    start() {
      const owner = activeStoreOwner();
      if (current && current.owner === owner) return current.run;
      const entry = {
        owner,
        run: (current?.run ?? Promise.resolve())
          // Un compte remplacé pendant l'attente ne travaille plus : le suivant le fera.
          .then(() => (accountChanged(owner) ? undefined : task(owner)))
          // Échec du stockage lui-même (base fermée par une déconnexion) : dit, jamais une promesse rejetée.
          .catch((err: unknown) => notify({ status: 'error', lastError: (err as Error).message }))
          .finally(() => { if (current === entry) current = undefined; }),
      };
      current = entry;
      return entry.run;
    },
    running: () => current?.run,
  };
}

/** Gestes du rapport qu'une clôture en file attend (`afterOpIds`, WEB1-13). */
function closureAfter(op: OutboxOperation): string[] {
  const ids = op.patch.afterOpIds;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/** Médias locaux qu'une opération attend encore (photos d'une note hors ligne). */
function mediaRefs(op: OutboxOperation): string[] {
  const refs = op.patch.mediaLocalIds;
  return Array.isArray(refs) ? refs.filter((r): r is string => typeof r === 'string') : [];
}

/**
 * Remplace les médias locaux d'une opération par leurs identifiants serveur.
 * Tant qu'une photo n'est pas en ligne, l'opération attend : la note ne part
 * jamais sans les photos que le technicien y a mises. Le patch résolu est
 * enregistré, pour qu'un nouvel essai n'ait plus besoin des fichiers locaux.
 */
async function resolveMedia(
  store: Awaited<ReturnType<typeof getLocalStore>>,
  op: OutboxOperation,
): Promise<OutboxOperation | undefined> {
  const refs = mediaRefs(op);
  if (refs.length === 0) return op;
  const fileIds = Array.isArray(op.patch.fileIds) ? [...(op.patch.fileIds as string[])] : [];
  for (const localId of refs) {
    const media = await store.getMedia(localId);
    if (!media) continue; // déjà résolu et nettoyé lors d'un essai précédent
    // Photo refusée par le serveur : le geste part sans elle ; elle reste
    // affichée « refusée », avec son motif, dans les envois en attente.
    if (media.status === 'refusee') continue;
    if (media.status !== 'succes' || !media.serverFileId) return undefined;
    fileIds.push(media.serverFileId);
  }
  const { mediaLocalIds: _resolved, ...rest } = op.patch;
  const patch = { ...rest, fileIds };
  await store.updateOperation(op.opId, { patch });
  return { ...op, patch };
}

/**
 * Push par lots — idempotent (opId), conflits remontés visibles (Q116/Q123).
 * Un appel pendant un envoi du même compte attend ce même envoi ; juste après
 * un changement de compte, il part après celui de l'ancien.
 */
export function push(): Promise<void> {
  return pushRunner.start();
}

/** Refus du serveur : l'opération appartient à un autre compte que la session. */
const OTHER_ACCOUNT = 'autre_compte';

/** Gestes regardés à chaque envoi : assez pour dépasser ceux qui attendent leur délai. */
const SCAN_LIMIT = 500;
/** Gestes envoyés par lot. */
const BATCH_SIZE = 50;
const RETRY_BASE_MS = 30_000;
const RETRY_MAX_MS = 15 * 60_000;

/**
 * Délai avant le nouvel essai automatique d'un geste en erreur passagère
 * (serveur indisponible, erreur 5xx) : 30 s, puis le double à chaque échec,
 * plafonné à 15 min. Un serveur en panne n'est pas sollicité toutes les 25 s,
 * et le geste repart toujours seul.
 */
export function retryDelayMs(attempts: number): number {
  return Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1), RETRY_MAX_MS);
}

function nextAttemptAfter(attempts: number, now = Date.now()): string {
  return new Date(now + retryDelayMs(attempts)).toISOString();
}

/** Le délai d'un geste en erreur est-il écoulé ? */
function due(op: OutboxOperation, now: number): boolean {
  return !op.nextAttemptAt || Date.parse(op.nextAttemptAt) <= now;
}

/** Motif lisible d'un refus ou d'une erreur, tel que le serveur l'a donné. */
function reasonOf(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return fallback;
}

/** Même geste (même objet, même action) : une reprise réussie solde les refus précédents. */
function sameGesture(a: OutboxOperation, b: OutboxOperation): boolean {
  return a.entityKind === b.entityKind && a.entityId === b.entityId && a.opKind === b.opKind
    && a.patch.action === b.patch.action;
}

/** Geste confirmé : une clôture efface sa clé et solde ses refus antérieurs. */
async function settleSucceeded(store: LocalStore, op: OutboxOperation): Promise<void> {
  if (op.entityKind === 'time_entry' && op.opKind === 'update') {
    const acknowledge = await captureTimeCorrectionRefusals(op.entityId, op.opId);
    await acknowledge();
  }
  if (op.entityKind === 'intervention' && op.opKind === 'action' && op.patch.action === 'close') {
    clearClosureKeys(op.entityId);
    for (const refused of await store.listOperationsForEntity(op.entityId, ['refusee'])) {
      if (sameGesture(refused, op)) await store.updateOperation(refused.opId, { status: 'succes' });
    }
  }
}

async function pushBatch(owner: string | undefined): Promise<void> {
  const store = await getLocalStore();
  try {
    // Les photos d'abord : une opération qui les référence ne part qu'après elles.
    await uploadPendingMedia();
    if (accountChanged(owner)) return;
    // Un geste refusé (`refusee`) n'est jamais renvoyé seul : il attend une
    // correction et ne bloque plus les plus récents (WEB1-03).
    const candidates = await store.listOperations(['attente', 'erreur'], SCAN_LIMIT);
    // Une opération dont une photo n'est pas encore en ligne, ou dont le délai
    // de nouvel essai court, attend avec les suivantes de la MÊME entité (leur
    // ordre est préservé) ; elle ne bloque jamais le reste de la file.
    const batch: OutboxOperation[] = [];
    const waiting = new Set<string>();
    // Gestes qui ne partent pas dans ce lot : une clôture qui les cite attend
    // avec eux, elle ne part jamais avant le rapport qu'elle fige (WEB1-13).
    const held = new Set((await store.listOperations(['envoi'], SCAN_LIMIT)).map((op) => op.opId));
    const now = Date.now();
    for (const candidate of candidates) {
      if (batch.length >= BATCH_SIZE) break;
      const resolved = waiting.has(candidate.entityId)
        || !due(candidate, now)
        || closureAfter(candidate).some((opId) => held.has(opId))
        ? undefined
        : await resolveMedia(store, candidate);
      if (!resolved) {
        waiting.add(candidate.entityId);
        held.add(candidate.opId);
        continue;
      }
      batch.push(resolved);
    }
    if (batch.length === 0) return;
    notify({ status: 'pending' });
    for (const op of batch) await store.updateOperation(op.opId, { status: 'envoi' });
    const response = await api<{ results: { opId: string; result: string; detail?: unknown }[] }>(
      '/sync/push',
      {
        method: 'POST',
        body: {
          operations: batch.map((op) => ({
            opId: op.opId,
            entityKind: op.entityKind,
            entityId: op.entityId,
            opKind: op.opKind,
            baseVersion: op.baseVersion,
            base: op.base,
            patch: op.patch,
            ownerUserId: op.ownerUserId,
          })),
        },
      },
    );
    let conflicts = current.conflicts;
    for (const result of response.results) {
      const op = batch.find((b) => b.opId === result.opId);
      if (!op) continue;
      if (result.result === 'appliquee' || result.result === 'fusionnee') {
        // Avant le succès : les refus antérieurs se repèrent par rapport à ce geste encore « en envoi ».
        await settleSucceeded(store, op);
        await store.updateOperation(op.opId, { status: 'succes', nextAttemptAt: null });
      } else if (result.result === 'conflit') {
        await store.updateOperation(op.opId, {
          status: 'conflit',
          lastError: JSON.stringify(result.detail ?? {}),
        });
        conflicts += 1;
      } else if (result.result === 'rejetee' && result.detail === OTHER_ACCOUNT) {
        // Session changée pendant l'envoi : le geste attend son auteur, intact.
        await store.updateOperation(op.opId, { status: 'attente' });
      } else if (result.result === 'rejetee') {
        // Refus définitif (règle métier, droit, donnée) : le renvoyer rendrait le
        // même refus. Le geste sort de la file et le dit, avec le motif.
        await store.updateOperation(op.opId, {
          status: 'refusee',
          lastError: reasonOf(result.detail, 'Refusé par le serveur'),
          attempts: op.attempts + 1,
          nextAttemptAt: null,
        });
      } else {
        // Erreur passagère du serveur : nouvel essai seul, après un délai croissant.
        await store.updateOperation(op.opId, {
          status: 'erreur',
          lastError: reasonOf(result.detail, 'Erreur du serveur'),
          attempts: op.attempts + 1,
          nextAttemptAt: nextAttemptAfter(op.attempts + 1),
        });
      }
    }
    // Nettoyage des succès anciens (> 24 h) — la file reste consultable (Q117)
    const cutoff = new Date(Date.now() - 86_400_000).toISOString();
    await store.purgeSucceededBefore(cutoff);
    notify({ status: 'ok', conflicts, lastSyncAt: new Date().toISOString() });
  } catch (err) {
    const message = (err as Error).message;
    const isNetwork = (err as { code?: string }).code === 'network_error';
    // Réseau absent : retour en attente, reprise au retour du réseau. Erreur du
    // serveur sur tout le lot : nouvel essai après un délai croissant.
    const stuck = await store.listOperations(['envoi'], 1000);
    for (const op of stuck) {
      await store.updateOperation(op.opId, isNetwork
        ? { status: 'attente' }
        : {
            status: 'erreur', lastError: message, attempts: op.attempts + 1,
            nextAttemptAt: nextAttemptAfter(op.attempts + 1),
          });
    }
    notify({ status: isNetwork ? 'offline' : 'error', lastError: message });
  } finally {
    await refreshCounters();
  }
}

const pushRunner = accountRunner(pushBatch);

/** Gestes et médias que l'envoi peut encore faire partir (un refus attend une correction). */
async function countSendable(): Promise<number> {
  const store = await getLocalStore();
  return (await store.countOperations(['attente', 'erreur', 'envoi']))
    + (await store.countMedia(['attente', 'erreur', 'envoi']));
}

/** Plafond de lots d'un envoi demandé (50 gestes et 5 médias par lot). */
const DRAIN_ROUNDS = 20;

/** Un envoi demandé n'attend pas les délais : les gestes en erreur repartent tout de suite. */
async function clearRetryDelays(): Promise<void> {
  const store = await getLocalStore();
  for (const op of await store.listOperations(['erreur'], SCAN_LIMIT)) {
    if (op.nextAttemptAt) await store.updateOperation(op.opId, { nextAttemptAt: null });
  }
}

/**
 * Envoi demandé par l'utilisateur (« Envoyer maintenant ») : un lot ne fait
 * partir que 50 gestes et 5 médias, et un geste attend ses photos ; les lots
 * s'enchaînent donc tant que chacun fait partir quelque chose. Un envoi déjà
 * en cours est d'abord attendu, puis un nouveau part : rien d'ajouté entre-temps
 * n'est oublié. Les délais de nouvel essai sont levés. S'arrête au premier lot
 * sans progrès (réseau coupé, refus).
 */
export async function drainQueue(): Promise<void> {
  await pushRunner.running();
  await clearRetryDelays();
  let before = await countSendable();
  for (let round = 0; round < DRAIN_ROUNDS && before > 0; round += 1) {
    await push();
    const after = await countSendable();
    if (after >= before) return;
    before = after;
  }
}

/** Gestes d'un type donné dans la file du compte actif (prises en charge d'urgence…). */
export async function getQueuedOperations(
  entityKind: string, statuses: OutboxOperation['status'][],
): Promise<OutboxOperation[]> {
  const store = await getLocalStore();
  return (await store.listOperations(statuses, SCAN_LIMIT)).filter((op) => op.entityKind === entityKind);
}

/**
 * « Réessayer » un geste de la file. En erreur passagère : il repart tout de
 * suite. Refusé : le serveur garde son refus sous cet identifiant, donc une
 * copie part sous un nouvel identifiant (même contenu, même clé de clôture),
 * après correction ; l'ancien est soldé. Rend l'identifiant du geste envoyé.
 */
export async function retryOperation(opId: string): Promise<string | undefined> {
  const store = await getLocalStore();
  const op = (await store.listOperations(['erreur', 'refusee'], SCAN_LIMIT)).find((o) => o.opId === opId);
  if (!op) return undefined;
  if (op.status === 'erreur') {
    await store.updateOperation(opId, { status: 'attente', nextAttemptAt: null });
    await refreshCounters();
    void push();
    return opId;
  }
  // Une clôture reprise attend le rapport tel qu'il est en file maintenant,
  // pas les gestes déjà réglés ou refusés de son premier envoi.
  let patch = op.patch;
  if (isClose(op)) {
    const { afterOpIds: _previous, ...rest } = op.patch;
    const afterOpIds = await pendingReportOperations(op.entityId);
    patch = afterOpIds.length > 0 ? { ...rest, afterOpIds } : rest;
  }
  const copy: OutboxOperation = {
    opId: crypto.randomUUID(),
    entityKind: op.entityKind,
    entityId: op.entityId,
    opKind: op.opKind,
    ...(op.baseVersion === undefined ? {} : { baseVersion: op.baseVersion }),
    ...(op.base === undefined ? {} : { base: op.base }),
    patch,
    createdAt: new Date().toISOString(),
    status: 'attente',
    attempts: 0,
    ...(op.ownerUserId ? { ownerUserId: op.ownerUserId } : {}),
  };
  await store.addOperation(copy);
  await store.updateOperation(opId, { status: 'succes' });
  await refreshCounters();
  void push();
  return copy.opId;
}

/**
 * Préfixe de l'état du pull incrémental. Tout ce qui commence par ce préfixe
 * quitte l'appareil à la déconnexion.
 */
const PULL_CURSOR = 'lastPullAt.';
/**
 * Copies de lecture gardées par un écran (les derniers messages, lisibles sans réseau) :
 * des données du serveur, retirées à la déconnexion comme les fiches synchronisées.
 */
export const READ_COPY_PREFIX = 'copie.';
/** Heure de reprise du prochain pull. */
const PULL_SINCE = `${PULL_CURSOR}curseur`;
/**
 * Mode et signature du périmètre servi (`terrain:personnel`,
 * `complet:equipe+temps`…). Une seule clé pour les deux modes : la base locale
 * leur est commune et le mode Terrain la taille, donc changer de mode (ou de
 * droits) relit tout le périmètre au lieu d'un simple delta.
 */
const PULL_SIGNATURE = `${PULL_CURSOR}perimetre`;
/**
 * Type déjà comparé à la liste du serveur (`<préfixe><type>`) : empreinte du
 * serveur, empreinte locale et objets gardés pour un geste en file. Tant que
 * rien de cela ne bouge, la liste n'est pas relue (WEB1-36).
 */
const SCOPE_MARK = `${PULL_CURSOR}perimetre.`;
/**
 * Type déjà vérifié (`<préfixe><type>`) : empreinte du serveur et nombre de
 * copies de l'appareil à ce moment (`<empreinte>:<nombre>` quand ils sont
 * d'accord), plus l'empreinte des objets gardés pour un geste en file quand
 * seuls ceux-là font la différence (`<empreinte>:<nombre>:<gardés>`). Tant que
 * rien de cela ne bouge, le périmètre n'a pas changé : la base locale n'est ni
 * relue ni hachée (WEB1-36). Un pull qui ne fait que mettre à jour des copies
 * déjà présentes ne change pas le nombre ; une copie nouvelle ou retirée, si.
 */
const SCOPE_AGREED = `${PULL_CURSOR}empreinte.`;
/** Pages lues au plus par pull (250 lignes chacune) : au-delà, le pull s'arrête et le dit. */
const PULL_PAGES_MAX = 400;
/** Pages de la liste du périmètre (5 000 identifiants chacune). */
const SCOPE_PAGES_MAX = 100;

/** Une page de `GET /sync/pull` (FLD-3). */
interface PullPage {
  serverTime: string;
  /** Compte servi : une session changée entre-temps n'écrit pas dans la base d'un autre. */
  userId?: string;
  hasMore: boolean;
  cursor: string | null;
  /** Dernière page : signature du périmètre (droits) et empreinte de ses identifiants par type. */
  perimeter?: string;
  scopeHashes?: Record<string, string>;
  conflicts: { entity_id?: unknown }[];
  conflictsTotal?: number;
  interventions?: Record<string, unknown>[];
  customers?: Record<string, unknown>[];
  sites?: Record<string, unknown>[];
  equipments?: Record<string, unknown>[];
  reports?: Record<string, unknown>[];
  timeEntries?: Record<string, unknown>[];
  interventionNotes?: Record<string, unknown>[];
  interventionNeeds?: Record<string, unknown>[];
  scheduleEvents?: Record<string, unknown>[];
  articles?: Record<string, unknown>[];
  checklistTemplates?: Record<string, unknown>[];
  reportParts?: Record<string, unknown>[];
  reportMeasurements?: Record<string, unknown>[];
  signatures?: Record<string, unknown>[];
  signatureExceptions?: Record<string, unknown>[];
  vehicleStock?: Record<string, unknown>[];
  fluidTypes?: Record<string, unknown>[];
  vehicleCylinders?: Record<string, unknown>[];
  cerfaForms?: Record<string, unknown>[];
  fluidMovements?: Record<string, unknown>[];
  lastLeakChecks?: Record<string, unknown>[];
  leakDetectors?: Record<string, unknown>[];
  cerfaSettings?: Record<string, unknown>[];
}

type PullSection =
  | 'interventions' | 'customers' | 'sites' | 'equipments' | 'reports' | 'timeEntries'
  | 'interventionNotes' | 'interventionNeeds' | 'scheduleEvents' | 'articles' | 'checklistTemplates'
  | 'reportParts' | 'reportMeasurements' | 'signatures' | 'signatureExceptions' | 'vehicleStock'
  | 'fluidTypes' | 'vehicleCylinders' | 'cerfaForms' | 'fluidMovements' | 'lastLeakChecks' | 'leakDetectors'
  | 'cerfaSettings';

const PULL_SECTIONS: [string, PullSection][] = [
  ['intervention', 'interventions'],
  ['customer', 'customers'],
  ['site', 'sites'],
  ['equipment', 'equipments'],
  ['report', 'reports'],
  ['time_entry', 'timeEntries'],
  ['intervention_note', 'interventionNotes'],
  ['intervention_need', 'interventionNeeds'],
  ['schedule_event', 'scheduleEvents'],
  ['article', 'articles'],
  ['checklist_template', 'checklistTemplates'],
  // Rapport complet et stock de son véhicule hors ligne (décision 6).
  ['report_part', 'reportParts'],
  ['report_measurement', 'reportMeasurements'],
  ['signature', 'signatures'],
  ['signature_exception', 'signatureExceptions'],
  ['stock_level', 'vehicleStock'],
  // Fluides et CERFA hors ligne (FLD-8) : seulement avec le droit de les saisir.
  ['fluid_type', 'fluidTypes'],
  ['fluid_cylinder', 'vehicleCylinders'],
  ['cerfa_form', 'cerfaForms'],
  ['fluid_movement', 'fluidMovements'],
  ['leak_check', 'lastLeakChecks'],
  ['leak_detector', 'leakDetectors'],
  ['cerfa_settings', 'cerfaSettings'],
];

/**
 * Pull incrémental : met à jour le cache du périmètre préchargé (Q115). Un
 * appel pendant un pull du même compte attend ce même pull ; la déconnexion
 * l'attend aussi avant de ranger la base (`releaseAccountData`).
 */
export function pull(): Promise<void> {
  return pullRunner.start();
}

/**
 * Toutes les pages d'un pull, écrites au fur et à mesure ; `undefined` si le
 * compte a changé.
 */
async function pullPages(
  store: LocalStore, owner: string | undefined, since: string | undefined, terrain: boolean,
): Promise<PullPage | undefined> {
  let cursor: string | null = null;
  for (let page = 0; page < PULL_PAGES_MAX; page += 1) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    else if (since) params.set('since', since);
    if (terrain) params.set('scope', 'terrain');
    const query = params.toString();
    const data: PullPage = await api<PullPage>(`/sync/pull${query ? `?${query}` : ''}`);
    if (owner && data.userId && data.userId !== owner) return undefined;
    for (const [kind, key] of PULL_SECTIONS) {
      // Déconnexion ou autre compte pendant la requête : rien n'est réécrit
      // dans une base que la déconnexion vient de vider (WEB1-01).
      if (accountChanged(owner)) return undefined;
      const rows = data[key] ?? [];
      if (rows.length === 0) continue;
      await store.bulkPutEntities(rows.map((row): CachedEntity => ({
        id: String(row.id ?? `${kind}:${JSON.stringify(row).slice(0, 40)}`),
        kind,
        data: row,
        version: Number(row.version ?? 0),
        updatedAt: String(row.updated_at ?? data.serverTime),
      })));
    }
    if (!data.hasMore || !data.cursor) return data;
    cursor = data.cursor;
  }
  throw new Error('Synchronisation interrompue : trop de données à recevoir en une fois.');
}

/** Empreinte d'une liste d'identifiants, calculée comme le serveur (SHA-256 de « a,b,… » triés). */
export async function idsHash(ids: readonly string[]): Promise<string | undefined> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return undefined;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode([...ids].sort().join(',')));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Objets qu'un geste pas encore réglé concerne : jamais retirés de l'appareil. */
async function protectedEntities(store: LocalStore): Promise<Set<string>> {
  const operations = await store.listOperations(['attente', 'erreur', 'envoi', 'conflit', 'refusee'], UNSENT_LIMIT);
  const ids = new Set<string>();
  for (const op of operations) {
    ids.add(op.entityId);
    const intervention = interventionOf(op);
    if (intervention) ids.add(intervention);
  }
  return ids;
}

/** Repère d'un type comparé à la liste du serveur (voir `SCOPE_MARK`). */
async function scopeMark(expected: string, ids: readonly string[], keep: Set<string>): Promise<string> {
  return `${expected}:${await idsHash(ids)}:${await idsHash(ids.filter((id) => keep.has(id)))}`;
}

/**
 * Suppressions et sorties de périmètre (API1-16) : pour chaque type dont
 * l'empreinte diffère de celle du serveur, la liste du périmètre est lue et
 * ce qui n'y est plus quitte l'appareil — sauf une création pas encore
 * confirmée et un objet qu'un geste en file concerne encore. Un type où seul
 * un objet gardé fait la différence n'est relu qu'une fois, jusqu'à ce que le
 * serveur, l'appareil ou le geste change.
 *
 * Un type déjà vérifié (`SCOPE_AGREED`) n'est pas relu tant que son périmètre
 * n'a pas changé (empreinte du serveur, nombre de copies, objets gardés) : le
 * pull de routine, toutes les 25 s, ne relit ni ne hache la base locale.
 *
 * Rend `true` si la liste cite des objets absents de l'appareil : la copie
 * locale a un trou qu'un delta ne comblera pas, le prochain pull relit tout.
 */
async function pruneOutOfScope(
  store: LocalStore, owner: string | undefined, terrain: boolean, hashes: Record<string, string>,
): Promise<boolean> {
  const local = new Map<string, string[]>();
  const counts = new Map<string, number>();
  const differing: string[] = [];
  // Objets gardés pour un geste en file : lus une fois, seulement si un type en dépend.
  let keptNow: { ids: Set<string>; hash: string | undefined } | undefined;
  const kept = async (): Promise<{ ids: Set<string>; hash: string | undefined }> => {
    if (!keptNow) {
      const ids = await protectedEntities(store);
      keptNow = { ids, hash: await idsHash([...ids]) };
    }
    return keptNow;
  };
  for (const [kind, expected] of Object.entries(hashes)) {
    const checked = await store.getMeta(`${SCOPE_AGREED}${kind}`);
    const count = await store.countEntitiesByKind(kind);
    if (checked === `${expected}:${count}`) continue;
    if (checked?.startsWith(`${expected}:${count}:`) && checked === `${expected}:${count}:${(await kept()).hash}`) continue;
    const rows = await store.listEntitiesByKind(kind);
    const ids = rows.filter((row) => !row.data.pending).map((row) => row.id);
    if ((await idsHash(ids)) === expected) {
      await store.putMeta(`${SCOPE_AGREED}${kind}`, `${expected}:${rows.length}`);
      continue;
    }
    if (checked !== undefined) await store.deleteMeta(`${SCOPE_AGREED}${kind}`);
    local.set(kind, ids);
    counts.set(kind, rows.length);
    differing.push(kind);
  }
  if (differing.length === 0) return false;
  const before = await kept();
  const stale: string[] = [];
  for (const kind of differing) {
    const ids = local.get(kind) ?? [];
    const mark = await scopeMark(hashes[kind]!, ids, before.ids);
    if ((await store.getMeta(`${SCOPE_MARK}${kind}`)) !== mark) stale.push(kind);
    // Seuls des objets gardés font la différence, déjà vus ainsi : plus relu
    // tant que le serveur, l'appareil ou la file ne bouge pas.
    else await store.putMeta(`${SCOPE_AGREED}${kind}`, `${hashes[kind]!}:${counts.get(kind) ?? 0}:${before.hash}`);
  }
  if (stale.length === 0) return false;
  const inScope = new Map<string, Set<string>>(stale.map((kind) => [kind, new Set<string>()]));
  let cursor: string | null = null;
  for (let page = 0; page < SCOPE_PAGES_MAX; page += 1) {
    const params = new URLSearchParams({ kinds: stale.join(',') });
    if (terrain) params.set('scope', 'terrain');
    if (cursor) params.set('cursor', cursor);
    const data: { ids: Record<string, string[]>; cursor: string | null } =
      await api<{ ids: Record<string, string[]>; cursor: string | null }>(`/sync/scope?${params.toString()}`);
    for (const [kind, ids] of Object.entries(data.ids)) for (const id of ids) inScope.get(kind)?.add(id);
    cursor = data.cursor;
    if (!cursor) break;
  }
  // Liste incomplète : rien n'est retiré sur une vue partielle.
  if (cursor || accountChanged(owner)) return false;
  const keep = await protectedEntities(store);
  let missing = false;
  for (const kind of stale) {
    const listed = inScope.get(kind) ?? new Set<string>();
    const ids = local.get(kind) ?? [];
    const gone = ids.filter((id) => !listed.has(id) && !keep.has(id));
    if (gone.length > 0) await store.deleteEntities(gone);
    const rows = await store.listEntitiesByKind(kind);
    const present = new Set(rows.map((row) => row.id));
    if ([...listed].some((id) => !present.has(id))) {
      missing = true;
      continue;
    }
    const goneIds = new Set(gone);
    const left = ids.filter((id) => !goneIds.has(id));
    // D'accord, ou en désaccord seulement par des objets gardés : plus relu tant que rien ne bouge.
    const agreed = (await idsHash(left)) === hashes[kind];
    await store.putMeta(
      `${SCOPE_AGREED}${kind}`,
      `${hashes[kind]!}:${rows.length}${agreed ? '' : `:${await idsHash([...keep])}`}`,
    );
    await store.putMeta(`${SCOPE_MARK}${kind}`, await scopeMark(hashes[kind]!, left, keep));
  }
  return missing;
}

async function pullOnce(owner: string | undefined): Promise<void> {
  const store = await getLocalStore();
  try {
    // En mode Terrain, ne précharger que SES missions (pas tout le tenant sur un
    // téléphone). Le serveur reste seul juge des droits — voir /sync/pull.
    const terrain = localStorage.getItem('fa.fieldmode') === 'terrain';
    const mode = terrain ? 'terrain' : 'complet';
    const signature = await store.getMeta(PULL_SIGNATURE);
    // Mode changé depuis le dernier pull : le mode Terrain a retiré de la base
    // commune ce que le Pilotage garde, sans que ces lignes aient changé ; un
    // delta ne les rendrait jamais, tout le périmètre est donc relu.
    const since = signature?.startsWith(`${mode}:`) ? await store.getMeta(PULL_SINCE) : undefined;
    let data = await pullPages(store, owner, since, terrain);
    if (!data) return;
    // Droits changés depuis le dernier pull (ou curseur d'une version
    // précédente) : ce qui vient d'entrer dans le périmètre n'a pas forcément
    // changé ; tout le périmètre est donc relu.
    if (since && `${mode}:${data.perimeter ?? ''}` !== signature) {
      data = await pullPages(store, owner, undefined, terrain);
      if (!data) return;
    }
    if (accountChanged(owner)) return;
    await store.putMeta(PULL_SINCE, data.serverTime);
    await store.putMeta(PULL_SIGNATURE, `${mode}:${data.perimeter ?? ''}`);
    if (data.scopeHashes && (await pruneOutOfScope(store, owner, terrain, data.scopeHashes))) {
      // Le serveur cite des objets que l'appareil n'a pas : le prochain pull relit tout.
      if (!accountChanged(owner)) await store.deleteMeta(PULL_SINCE);
    }
    if (accountChanged(owner)) return;
    // Conflit tranché par le bureau : le geste de l'appareil n'attend plus (Q183).
    // Liste tronquée (plus de 1 000 conflits ouverts) : rien n'est soldé à l'aveugle.
    const total = data.conflictsTotal ?? data.conflicts.length;
    if (data.conflicts.length === total) {
      const open = new Set(data.conflicts.map((conflict) => String(conflict.entity_id ?? '')));
      for (const op of await store.listOperations(['conflit'], SCAN_LIMIT)) {
        if (!open.has(op.entityId)) await store.updateOperation(op.opId, { status: 'succes' });
      }
    }
    notify({ status: 'ok', conflicts: total, lastSyncAt: new Date().toISOString() });
  } catch (err) {
    const isNetwork = (err as { code?: string }).code === 'network_error';
    notify({ status: isNetwork ? 'offline' : 'error', lastError: (err as Error).message });
  }
}

const pullRunner = accountRunner(pullOnce);

/**
 * Budget des fichiers gardés sur l'appareil en attendant le réseau (décision
 * 6) : un fichier de 25 Mo au plus (une photo réduite en fait 1 ou 2), 200
 * fichiers et 300 Mo au plus en attente pour le compte. Au-delà, rien n'est
 * gardé et l'écran le dit : mieux vaut retrouver du réseau que remplir le
 * téléphone. Un fichier refusé par le serveur ne compte pas dans ce budget
 * (il ne partira plus) et quitte l'appareil après 30 jours.
 */
export const MEDIA_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const MEDIA_QUEUE_MAX_FILES = 200;
export const MEDIA_QUEUE_MAX_BYTES = 300 * 1024 * 1024;
const REFUSED_MEDIA_MAX_AGE_MS = 30 * 86_400_000;
const QUEUED_MEDIA: PendingMedia['status'][] = ['attente', 'erreur', 'envoi'];

/** Fichier que l'appareil ne peut pas garder pour l'envoyer plus tard (budget dépassé). */
export class MediaBudgetError extends Error {
  readonly code = 'budget_appareil';
}

/**
 * File de médias (Q120) : la photo est réduite AVANT d'être gardée (même
 * réduction que l'envoi en ligne, WEB1-36), puis conservée sur l'appareil
 * jusqu'à l'envoi confirmé, dans le budget ci-dessus. Aucune preuve n'est
 * perdue : le serveur ré-encode de toute façon ce qu'il reçoit.
 */
export async function queueMedia(blob: Blob, filename: string, kind: string): Promise<string> {
  const stored = await shrinkForUpload(blob, kind);
  if (stored.size > MEDIA_FILE_MAX_BYTES) {
    throw new MediaBudgetError('Fichier trop volumineux pour attendre le réseau sur cet appareil (25 Mo au plus).');
  }
  const store = await getLocalStore();
  const usage = await store.mediaUsage(QUEUED_MEDIA);
  if (usage.count >= MEDIA_QUEUE_MAX_FILES || usage.bytes + stored.size > MEDIA_QUEUE_MAX_BYTES) {
    throw new MediaBudgetError(
      'Trop de fichiers attendent déjà le réseau sur cet appareil : envoyez-les avant d’en ajouter d’autres.',
    );
  }
  const localId = crypto.randomUUID();
  await store.addMedia({
    localId,
    blob: stored,
    filename: uploadFilename(filename, stored, blob),
    kind,
    status: 'attente',
    createdAt: new Date().toISOString(),
  });
  await refreshCounters();
  void uploadPendingMedia();
  return localId;
}

/**
 * Garde plusieurs fichiers pour un même geste : tous ou aucun. Si l'un dépasse
 * le budget, ceux déjà gardés par cet appel sont retirés de l'appareil avant
 * de relancer l'erreur, pour qu'aucun fichier ne reste sans geste qui le cite.
 */
export async function queueMediaAll(
  files: { blob: Blob; filename: string; kind: string }[],
): Promise<string[]> {
  const localIds: string[] = [];
  try {
    for (const file of files) localIds.push(await queueMedia(file.blob, file.filename, file.kind));
    return localIds;
  } catch (err) {
    const store = await getLocalStore();
    for (const localId of localIds) await store.deleteMedia(localId);
    await refreshCounters();
    throw err;
  }
}

/**
 * Réponses qui refusent un fichier pour de bon (le reste est passager : réseau,
 * serveur, quota). Un fichier trop gros rend `validation_error` (limite de
 * l'API) ou `payload_too_large` (413 du proxy, sans enveloppe : `PAYLOAD_TOO_LARGE`
 * de `core/api.ts`).
 */
const MEDIA_REFUSALS = new Set(['validation_error', 'forbidden', 'fichier_infecte', 'payload_too_large']);

/** Téléversement en cours : un second appel le rejoint (l'envoi des gestes attend ainsi ses photos). */
let uploadingMedia: Promise<void> | undefined;

/** Un média envoyé reste sur l'appareil tant qu'une opération en attente le cite. */
async function referencedMedia(
  store: Awaited<ReturnType<typeof getLocalStore>>,
): Promise<Set<string>> {
  const waiting = await store.listOperations(['attente', 'erreur', 'envoi', 'conflit'], 1000);
  return new Set(waiting.flatMap(mediaRefs));
}

/**
 * Téléverse les médias en attente du compte actif. Chaque envoi dit son compte
 * (`ownerUserId`) : si la session a changé entre-temps (autre onglet, autre
 * compte), le serveur le refuse et la photo attend son auteur, jamais rangée
 * dans le stockage d'un autre compte ni d'une autre entreprise (WEB1-01).
 */
export function uploadPendingMedia(): Promise<void> {
  uploadingMedia ??= uploadMediaBatch().finally(() => { uploadingMedia = undefined; });
  return uploadingMedia;
}

async function uploadMediaBatch(): Promise<void> {
  const owner = activeStoreOwner();
  const store = await getLocalStore();
  try {
    // Nettoyage local contrôlé après confirmation (Q120) : jamais tant qu'une
    // note hors ligne attend encore l'identifiant serveur de cette photo.
    const sent = await store.listMedia(['succes'], 50);
    if (sent.length > 0) {
      const keep = await referencedMedia(store);
      const cutoff = Date.now() - 60_000;
      for (const media of sent) {
        if (!keep.has(media.localId) && Date.parse(media.createdAt) < cutoff) {
          await store.deleteMedia(media.localId);
        }
      }
    }
    // Fichier refusé depuis plus de 30 jours : son motif a été affiché, il quitte l'appareil.
    const refusedCutoff = Date.now() - REFUSED_MEDIA_MAX_AGE_MS;
    for (const media of await store.listMedia(['refusee'], 50)) {
      if (Date.parse(media.createdAt) < refusedCutoff) await store.deleteMedia(media.localId);
    }
    const pending = await store.listMedia(['attente', 'erreur'], 5);
    for (const media of pending) {
      if (accountChanged(owner)) break;
      await store.updateMedia(media.localId, { status: 'envoi' });
      try {
        const { fileId } = await uploadFile(
          media.blob, media.kind, media.filename, undefined, owner ? { ownerUserId: owner } : {},
        );
        await store.updateMedia(media.localId, { status: 'succes', serverFileId: fileId });
      } catch (err) {
        if (err instanceof ApiError && err.details === OTHER_ACCOUNT) {
          // Session d'un autre compte : la photo attend intacte son auteur.
          await store.updateMedia(media.localId, { status: 'attente' });
          break;
        }
        const code = (err as { code?: string }).code;
        // Refus du fichier lui-même (type, taille, droit, antivirus) : le renvoyer
        // rendrait le même refus. Il reste sur l'appareil, avec le motif.
        const refused = code !== undefined && MEDIA_REFUSALS.has(code);
        await store.updateMedia(media.localId, {
          status: refused ? 'refusee' : 'erreur',
          lastError: (err as Error).message,
        });
        if (code === 'network_error') break;
      }
    }
  } finally {
    await refreshCounters();
  }
}

/** Une clôture de cette intervention attend-elle d'être envoyée ? */
function isClose(op: OutboxOperation): boolean {
  return op.entityKind === 'intervention' && op.opKind === 'action' && op.patch.action === 'close';
}

/**
 * Gestes du rapport faits sans réseau (décision 6) : photo, pièce, mesure,
 * signature, signature impossible. Mis en file sur l'intervention, ils partent avant la
 * clôture qui les suit, et la clôture les cite (`afterOpIds`).
 */
export const REPORT_GESTURES = [
  'report_photo', 'report_part', 'report_measurement', 'report_signature', 'signature_exception',
] as const;
export type ReportGesture = (typeof REPORT_GESTURES)[number];

export function isReportGesture(op: OutboxOperation): boolean {
  return op.entityKind === 'intervention' && op.opKind === 'action'
    && (REPORT_GESTURES as readonly unknown[]).includes(op.patch.action);
}

/**
 * Lecture locale : cache + modifications en attente fusionnées (état vu par
 * l'UI). D'une étape en file, seul l'état est fusionné, jamais la position
 * relevée ; une clôture en file se lit `closure_pending` (WEB1-14). Un geste
 * refusé n'est plus fusionné.
 */
export async function readEntity<T = Record<string, unknown>>(
  entityId: string,
): Promise<T | undefined> {
  const store = await getLocalStore();
  const cached = await store.getEntity(entityId);
  if (!cached || cached.data.deleted_at) return undefined;
  const pendingOps = await store.listOperationsForEntity(
    entityId, ['attente', 'erreur', 'envoi'],
  );
  let data = { ...cached.data };
  for (const op of pendingOps) {
    if (op.opKind === 'update' || op.opKind === 'create') data = { ...data, ...op.patch };
    // D'une étape en file, seul l'état compte : la position relevée n'est pas
    // l'adresse du client (WEB1-05) ; un pull ne remet pas l'ancien état.
    else if (op.opKind === 'action' && op.patch.action === 'transition' && typeof op.patch.to === 'string') {
      data = { ...data, status: op.patch.to };
    }
  }
  if (pendingOps.some(isClose)) data = { ...data, closure_pending: true };
  return data as T;
}

/** Ce que l'appareil sait d'une intervention, en plus du serveur : clôture en file ou refusée. */
export interface LocalInterventionState {
  closure?: { state: 'pending' } | { state: 'refused'; reason: string; opId: string };
}

/**
 * État local d'une intervention, lu en ligne comme hors ligne : la fiche du
 * serveur ignore qu'une clôture attend l'envoi sur ce téléphone, ou qu'elle a
 * été refusée (WEB1-03, WEB1-14).
 */
export async function localInterventionState(interventionId: string): Promise<LocalInterventionState> {
  const store = await getLocalStore();
  const operations = (await store.listOperationsForEntity(interventionId, ['attente', 'erreur', 'envoi', 'refusee']))
    .filter(isClose);
  if (operations.some((op) => op.status !== 'refusee')) return { closure: { state: 'pending' } };
  const refused = operations.at(-1);
  return refused
    ? { closure: { state: 'refused', reason: refused.lastError ?? 'Refusée par le serveur', opId: refused.opId } }
    : {};
}

/**
 * Gestes du rapport de cette intervention encore à envoyer (en attente, en
 * erreur passagère ou en cours d'envoi) : modifications du rapport, photos,
 * pièces et signatures. Une clôture les attend (WEB1-13, WEB1-07).
 */
export async function pendingReportOperations(interventionId: string): Promise<string[]> {
  return reportOperations(interventionId, ['attente', 'erreur', 'envoi']);
}

/**
 * Une modification de ce rapport attend encore dans la file (en attente, en
 * erreur passagère ou en cours d'envoi). La suivante la suit dans la file au
 * lieu de partir en ligne : sa base est la valeur mise en file, que le serveur
 * n'a pas encore, et un envoi direct ouvrirait un faux conflit (FLD-9).
 */
export async function hasQueuedReportUpdate(reportId: string): Promise<boolean> {
  const store = await getLocalStore();
  return (await store.listOperations(['attente', 'erreur', 'envoi'], SCAN_LIMIT))
    .some((op) => op.entityKind === 'report' && op.opKind === 'update' && op.entityId === reportId);
}

/**
 * Gestes du rapport refusés ou en conflit : la saisie hors ligne n'est pas sur
 * le serveur. Une clôture en ligne figerait le rapport sans elle ; ils se
 * règlent d'abord dans « Envois en attente ».
 */
export async function blockedReportOperations(interventionId: string): Promise<string[]> {
  return reportOperations(interventionId, ['refusee', 'conflit']);
}

async function reportOperations(interventionId: string, statuses: OutboxOperation['status'][]): Promise<string[]> {
  const store = await getLocalStore();
  const opIds: string[] = [];
  for (const op of await store.listOperations(statuses, SCAN_LIMIT)) {
    if (isReportGesture(op)) {
      if (op.entityId === interventionId) opIds.push(op.opId);
      continue;
    }
    if (op.entityKind !== 'report') continue;
    const report = await store.getEntity(op.entityId);
    if (interventionOf(op, report?.data) === interventionId) opIds.push(op.opId);
  }
  return opIds;
}

/**
 * Clôture mise en file (décision 6) : elle suit le rapport dans la file et
 * cite ses gestes encore à envoyer. Elle ne part qu'après eux, et le serveur
 * la refuse si l'un n'a pas été enregistré : jamais un rapport figé sans la
 * saisie du technicien.
 */
export async function enqueueClosure(
  interventionId: string,
  closure: { closureKey: string; occurredAt: string },
): Promise<string> {
  const afterOpIds = await pendingReportOperations(interventionId);
  return enqueueOperation({
    entityKind: 'intervention',
    entityId: interventionId,
    opKind: 'action',
    patch: { action: 'close', ...closure, offline: true, ...(afterOpIds.length > 0 ? { afterOpIds } : {}) },
  });
}

/** Clôture refusée reprise par un nouvel appui confirmé : le refus est soldé. */
export async function settleRefusedClosure(interventionId: string): Promise<void> {
  const store = await getLocalStore();
  for (const op of await store.listOperationsForEntity(interventionId, ['refusee'])) {
    if (isClose(op)) await store.updateOperation(op.opId, { status: 'succes' });
  }
  await refreshCounters();
}

export async function listEntities<T = Record<string, unknown>>(kind: string): Promise<T[]> {
  const store = await getLocalStore();
  const rows = await store.listEntitiesByKind(kind);
  if (kind === 'time_entry')
    return Promise.all(
      rows.filter((row) => !row.data.deleted_at).map(async (row) => {
        const pending = await store.listOperationsForEntity(row.id, ['attente', 'erreur', 'envoi']);
        const refused = await store.listOperationsForEntity(row.id, ['refusee']);
        const refusedCreate = refused.find((op) => op.opKind === 'create');
        let data: Record<string, unknown> = {
          ...row.data, pending: pending.length > 0, refused: Boolean(refusedCreate),
          // Refusée pour chevauchement (file d'avant le 15/10/2026) : « Enregistrer quand même ».
          refusedOverlap: Boolean(refusedCreate && /chevauche/i.test(refusedCreate.lastError ?? '')),
          correctionRefused: refused.some((op) => op.opKind !== 'create'),
        };
        for (const op of pending) data = { ...data, ...op.patch, pending: true };
        return data as T;
      }),
    );
  return Promise.all(rows.filter((row) => !row.data.deleted_at).map(async (row) => {
    if (!row.data.pending) return row.data as T;
    // Création hors ligne refusée (note, « à prévoir ») : marquée refusée, avec le motif,
    // au lieu de rester « en attente » pour toujours (WEB1-03).
    const refused = (await store.listOperationsForEntity(row.id, ['refusee'])).find((op) => op.opKind === 'create');
    return (refused
      ? { ...row.data, pending: false, refused: true, refusedReason: refused.lastError ?? 'Refusée par le serveur' }
      : row.data) as T;
  }));
}

// --- Envois en attente (WEB1-21) ---------------------------------------------------------

/** Ce que la page « Envois en attente » montre : gestes et fichiers pas encore réglés. */
export interface QueueEntry {
  id: string;
  kind: 'operation' | 'media';
  status: OutboxOperation['status'] | PendingMedia['status'];
  createdAt: string;
  reason?: string;
  nextAttemptAt?: string;
  operation?: OutboxOperation;
  media?: { kind: string; filename: string };
  /** Données de l'appareil sur l'objet concerné (client, site), pour le reconnaître. */
  context?: Record<string, unknown>;
}

const QUEUE_OPERATIONS: OutboxOperation['status'][] = ['attente', 'envoi', 'erreur', 'refusee', 'conflit'];
const QUEUE_MEDIA: PendingMedia['status'][] = ['attente', 'envoi', 'erreur', 'refusee'];
const QUEUE_LIMIT = 500;

/** Objet de rattachement d'un geste : l'intervention qu'il concerne, quand on la connaît. */
export function interventionOf(op: OutboxOperation, report?: Record<string, unknown>): string | undefined {
  if (op.entityKind === 'intervention') return op.entityId;
  const fromPatch = op.patch.interventionId ?? op.patch.intervention_id;
  if (typeof fromPatch === 'string') return fromPatch;
  const fromReport = report?.intervention_id;
  return typeof fromReport === 'string' ? fromReport : undefined;
}

/** L'intervention telle que l'appareil la connaît, avec le nom du client et du site. */
async function describeIntervention(store: LocalStore, data: Record<string, unknown>): Promise<Record<string, unknown>> {
  const customer = typeof data.customer_id === 'string' && !data.customer_name
    ? await store.getEntity(data.customer_id) : undefined;
  const site = typeof data.site_id === 'string' && !data.site_label ? await store.getEntity(data.site_id) : undefined;
  return {
    ...data,
    ...(customer?.data.display_name ? { customer_name: customer.data.display_name } : {}),
    ...(site?.data.label ? { site_label: site.data.label } : {}),
  };
}

/** La file du compte actif, lue sur l'appareil (hors ligne compris), plus anciens d'abord. */
export async function listQueue(): Promise<QueueEntry[]> {
  const store = await getLocalStore();
  const entries: QueueEntry[] = [];
  for (const op of await store.listOperations(QUEUE_OPERATIONS, QUEUE_LIMIT)) {
    const own = await store.getEntity(op.entityId);
    const interventionId = interventionOf(op, own?.data);
    const intervention = interventionId && interventionId !== op.entityId
      ? await store.getEntity(interventionId)
      : op.entityKind === 'intervention' ? own : undefined;
    entries.push({
      id: op.opId,
      kind: 'operation',
      status: op.status,
      createdAt: op.createdAt,
      ...(op.lastError && op.status !== 'attente' && op.status !== 'envoi' ? { reason: op.lastError } : {}),
      ...(op.status === 'erreur' && op.nextAttemptAt ? { nextAttemptAt: op.nextAttemptAt } : {}),
      operation: op,
      ...(intervention
        ? { context: await describeIntervention(store, intervention.data) }
        : op.entityKind === 'quote_draft' && typeof op.patch.customerId === 'string'
          // Devis préparé sans réseau, hors intervention : son client suffit à le reconnaître.
          ? { context: await describeIntervention(store, { customer_id: op.patch.customerId }) }
          : {}),
    });
  }
  for (const media of await store.listMedia(QUEUE_MEDIA, QUEUE_LIMIT)) {
    entries.push({
      id: media.localId,
      kind: 'media',
      status: media.status,
      createdAt: media.createdAt,
      ...(media.lastError && (media.status === 'erreur' || media.status === 'refusee') ? { reason: media.lastError } : {}),
      media: { kind: media.kind, filename: media.filename },
    });
  }
  return entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Retirer un geste ou un fichier refusé de l'appareil, après confirmation : le
 * serveur l'a refusé, rien n'est perdu côté entreprise. Seul un refus se
 * retire ; ce qui attend encore l'envoi ne s'abandonne jamais (décision 6).
 */
export async function discardRefused(entry: Pick<QueueEntry, 'id' | 'kind'>): Promise<boolean> {
  const store = await getLocalStore();
  if (entry.kind === 'media') {
    const media = await store.getMedia(entry.id);
    if (media?.status !== 'refusee') return false;
    await store.deleteMedia(entry.id);
  } else {
    const op = (await store.listOperations(['refusee'], QUEUE_LIMIT)).find((o) => o.opId === entry.id);
    if (!op) return false;
    await store.updateOperation(op.opId, { status: 'succes' });
    // Création refusée retirée : sa copie « en attente » quitte aussi l'écran.
    const created = op.opKind === 'create' ? await store.getEntity(op.entityId) : undefined;
    if (created?.data.pending) {
      await store.putEntity({ ...created, data: { ...created.data, deleted_at: new Date().toISOString() } });
    }
  }
  await refreshCounters();
  return true;
}

/**
 * Un envoi interrompu (application fermée en plein téléversement) laisse le
 * média « en cours d'envoi » pour toujours : au démarrage, rien n'est en cours,
 * il redevient donc « en attente » et repart.
 */
export async function resumeInterruptedMedia(): Promise<void> {
  const store = await getLocalStore();
  for (const media of await store.listMedia(['envoi'], 1000)) {
    await store.updateMedia(media.localId, { status: 'attente' });
  }
}

// --- Changement et fin de session d'un compte (WEB1-01) --------------------------

/**
 * Désigne la base du compte connecté (`undefined` : aucun). Changer de compte
 * repart de ses propres compteurs : rien de l'ancien compte ne reste affiché.
 */
export function switchAccount(scope: StoreScope | undefined): void {
  if (!selectStoreScope(scope)) return;
  current = IDLE;
  for (const listener of listeners) listener(current);
  if (scope) void refreshCounters();
}

/** Nature d'un envoi qui attend encore, pour l'annoncer avant la déconnexion. */
export type UnsentKind =
  | 'etape' | 'cloture' | 'intervention' | 'rapport' | 'note' | 'aPrevoir' | 'temps'
  | 'fluides' | 'devis' | 'message' | 'annonce' | 'refusee' | 'photo' | 'fichier' | 'autre';

export interface UnsentGroup {
  kind: UnsentKind;
  count: number;
}

/** Gestes pas encore partis (une opération refusée attend une correction). */
const UNSENT_OPERATIONS = ['attente', 'erreur', 'envoi', 'refusee'] as const;
const UNSENT_MEDIA = ['attente', 'erreur', 'envoi', 'refusee'] as const;
const UNSENT_LIMIT = 10_000;
const DRAFT_PREFIX = 'draft.v1.';
const UNSENT_ORDER: UnsentKind[] = [
  'cloture', 'etape', 'rapport', 'fluides', 'devis', 'note', 'photo', 'fichier', 'temps', 'aPrevoir',
  'intervention', 'message', 'annonce', 'autre', 'refusee',
];

function unsentKind(op: OutboxOperation): UnsentKind {
  if (op.status === 'refusee') return 'refusee';
  switch (op.entityKind) {
    case 'intervention':
      if (op.opKind !== 'action') return 'intervention';
      if (isReportGesture(op)) return 'rapport';
      return op.patch.action === 'close' ? 'cloture' : 'etape';
    case 'report': return 'rapport';
    case 'intervention_note': return 'note';
    case 'intervention_need': return 'aPrevoir';
    case 'time_entry': return 'temps';
    case 'fluid_movement':
    case 'leak_check':
    case 'cerfa_form': return 'fluides';
    case 'quote_draft': return 'devis';
    case 'message': return 'message';
    case 'announcement': return 'annonce';
    default: return 'autre';
  }
}

/** Ce que le compte actif n'a pas encore envoyé, regroupé par nature. */
export async function summarizeUnsentWork(): Promise<UnsentGroup[]> {
  const store = await getLocalStore();
  const counts = new Map<UnsentKind, number>();
  const add = (kind: UnsentKind): void => { counts.set(kind, (counts.get(kind) ?? 0) + 1); };
  for (const op of await store.listOperations([...UNSENT_OPERATIONS], UNSENT_LIMIT)) add(unsentKind(op));
  for (const media of await store.listMedia([...UNSENT_MEDIA], UNSENT_LIMIT)) {
    add(media.kind === 'photo' ? 'photo' : 'fichier');
  }
  return UNSENT_ORDER.filter((kind) => counts.has(kind))
    .map((kind) => ({ kind, count: counts.get(kind)! }));
}

/**
 * Un formulaire ouvert puis laissé intact écrit un brouillon vide
 * (`useDraftBackup`) : seul un brouillon qui contient du texte retient la base.
 */
async function keepsAccountWork(store: LocalStore): Promise<boolean> {
  return (await store.countOperations([...UNSENT_OPERATIONS])) > 0
    || (await store.countMedia([...UNSENT_MEDIA])) > 0
    || (await store.listMeta(DRAFT_PREFIX)).some((draft) => draft.value !== '');
}

/**
 * Envois qui attendent dans la base d'une autre entreprise du compte (ou de
 * l'actuelle) : ils y restent et partiront quand elle sera de nouveau active.
 */
export function waitingWorkFor(userId: string): Promise<number> {
  return countWaitingIn({ kind: 'account', userId }, [...UNSENT_OPERATIONS], [...UNSENT_MEDIA]);
}

/**
 * Attend l'envoi et le pull en cours : aucun n'écrit plus après le rangement
 * (déconnexion) ni après un changement d'entreprise.
 */
export async function settleAccountRuns(): Promise<void> {
  // Borné : la boucle de synchronisation, arrêtée par la déconnexion, ne relance plus.
  for (let round = 0; round < 5 && (pushRunner.running() || pullRunner.running()); round += 1) {
    await Promise.allSettled([pushRunner.running(), pullRunner.running()]);
  }
}

/**
 * Déconnexion : les copies des données du serveur (fiches, clients, planning,
 * derniers messages, curseurs du pull) quittent l'appareil. Les gestes pas encore envoyés, leurs
 * photos et les brouillons du compte restent dans SA base, que nul autre compte
 * ne lit, et partent à sa prochaine connexion (décision 6 : jamais d'abandon).
 * S'il ne reste rien, la base est supprimée. `erase` supprime tout, après la
 * confirmation explicite de l'écran de déconnexion ; une assistance MyEquip
 * efface toujours la sienne.
 */
export async function releaseAccountData(scope: StoreScope, mode: 'keep' | 'erase'): Promise<void> {
  // Un envoi ou un pull lancé juste avant la déconnexion finit d'abord d'écrire
  // (le pull, voyant le compte changé, n'écrit plus rien).
  await settleAccountRuns();
  const store = await openLocalStore(scope);
  if (mode === 'erase' || scope.kind === 'support' || !(await keepsAccountWork(store))) {
    await destroyLocalStore(scope);
    return;
  }
  await store.clearEntities();
  for (const cursor of await store.listMeta(PULL_CURSOR)) await store.deleteMeta(cursor.key);
  for (const copy of await store.listMeta(READ_COPY_PREFIX)) await store.deleteMeta(copy.key);
  for (const draft of await store.listMeta(DRAFT_PREFIX)) {
    if (draft.value === '') await store.deleteMeta(draft.key);
  }
  await store.purgeSucceededBefore(new Date(Date.now() + 60_000).toISOString());
  const keep = await referencedMedia(store);
  for (const media of await store.listMedia(['succes'], UNSENT_LIMIT)) {
    if (!keep.has(media.localId)) await store.deleteMedia(media.localId);
  }
}

/** Démarrage : reprise auto au retour du réseau + sync périodique (< 30 s — Q144). */
export function startSyncLoop(): () => void {
  const onOnline = (): void => {
    notify({ status: 'pending' });
    void push().then(() => pull());
    void uploadPendingMedia();
  };
  const onOffline = (): void => notify({ status: 'offline' });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  // Application en arrière-plan : aucun échange périodique (batterie, données,
  // WEB1-36) ; elle se resynchronise dès qu'elle revient au premier plan.
  const hidden = (): boolean => document.visibilityState === 'hidden';
  const interval = setInterval(() => {
    if (navigator.onLine && !hidden()) onOnline();
  }, 25_000);
  const onVisible = (): void => {
    if (navigator.onLine && !hidden()) onOnline();
  };
  document.addEventListener('visibilitychange', onVisible);
  void resumeInterruptedMedia().finally(() => {
    if (navigator.onLine) onOnline();
    else notify({ status: 'offline' });
  });
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(interval);
  };
}
